<template>
  <v-dialog
    transition="dialog-top-transition"
    max-width="760px"
    v-model="dialogModel"
    @click:outside="closeDialog"
    @keydown.esc="closeDialog"
  >
    <v-card class="assignments-fields-modal">
      <div class="assignments-fields-modal__header">
        <v-icon small class="mr-2">mdi-format-list-bulleted</v-icon>
        <h3 class="mb-0">Assignments fields</h3>
      </div>

      <div class="assignments-fields-modal__content">
        <p class="assignments-fields-modal__section-title mb-2">Shared</p>
        <div
          class="assignments-fields-modal__list"
          @dragover.prevent="onSectionDragOver('shared')"
          @drop.prevent="onSectionDrop('shared')"
        >
          <div
            v-for="(field, index) in localSharedFields"
            :key="`shared-${field}-${index}`"
            class="assignments-fields-modal__item"
            :class="[
              getDropClass('shared', index),
              { 'assignments-fields-modal__item--locked': !canMoveToIndividual(field) }
            ]"
            :draggable="isItemDraggable('shared', field)"
            @dragstart="onDragStart($event, 'shared', index)"
            @dragend="onDragEnd"
            @dragover.prevent="onDragOver('shared', index)"
            @drop.prevent="onDrop('shared', index)"
          >
            <v-icon
              class="assignments-fields-modal__drag-icon"
              small
            >
              mdi-drag
            </v-icon>
            <span>{{ field }}</span>
          </div>
        </div>

        <p class="assignments-fields-modal__section-title mt-4 mb-2">Individual</p>
        <div
          class="assignments-fields-modal__list"
          @dragover.prevent="onSectionDragOver('individual')"
          @drop.prevent="onSectionDrop('individual')"
        >
          <div
            v-for="(field, index) in localIndividualFields"
            :key="`individual-${field}-${index}`"
            class="assignments-fields-modal__item"
            :class="getDropClass('individual', index)"
            :draggable="isItemDraggable('individual', field)"
            @dragstart="onDragStart($event, 'individual', index)"
            @dragend="onDragEnd"
            @dragover.prevent="onDragOver('individual', index)"
            @drop.prevent="onDrop('individual', index)"
          >
            <v-icon
              class="assignments-fields-modal__drag-icon"
              small
            >
              mdi-drag
            </v-icon>
            <span>{{ field }}</span>
          </div>
        </div>
      </div>

      <v-card-actions class="justify-end mt-4">
        <v-btn depressed @click="closeDialog">Cancel</v-btn>
        <v-btn color="#1976D2" class="white--text" @click="saveAndClose">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "AssignmentsFieldsModal",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    sharedFields: {
      type: Array,
      default: () => [],
    },
    individualFields: {
      type: Array,
      default: () => [],
    },
    individualEligibleFields: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      localSharedFields: [],
      localIndividualFields: [],
      dragState: null,
      dropState: {
        section: null,
        index: null,
      },
    };
  },
  watch: {
    value(newValue) {
      if (newValue) {
        this.syncLocalFields();
      }
    },
    sharedFields: {
      handler() {
        this.syncLocalFields();
      },
      deep: true,
      immediate: true,
    },
    individualFields: {
      handler() {
        this.syncLocalFields();
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    dialogModel: {
      get() {
        return this.value;
      },
      set(newValue) {
        this.$emit("input", newValue);
      },
    },
  },
  methods: {
    syncLocalFields() {
      this.localSharedFields = Array.isArray(this.sharedFields)
        ? [...this.sharedFields]
        : [];
      this.localIndividualFields = Array.isArray(this.individualFields)
        ? [...this.individualFields]
        : [];
    },
    closeDialog() {
      this.dialogModel = false;
      this.clearDragState();
    },
    saveAndClose() {
      this.$emit("save", {
        sharedFields: [...this.localSharedFields],
        individualFields: [...this.localIndividualFields],
      });
      this.closeDialog();
    },
    getSectionList(section) {
      return section === "shared" ? this.localSharedFields : this.localIndividualFields;
    },
    onDragStart(event, section, index) {
      const sourceList = this.getSectionList(section);
      const fieldName = sourceList?.[index];
      if (!this.isItemDraggable(section, fieldName)) {
        if (event?.preventDefault) event.preventDefault();
        return;
      }
      if (event?.dataTransfer) {
        event.dataTransfer.setData("text/plain", "");
        event.dataTransfer.effectAllowed = "move";
      }
      this.dragState = { section, index };
    },
    onDragOver(section, index) {
      if (!this.dragState) return;
      this.dropState = { section, index };
    },
    onSectionDragOver(section) {
      if (!this.dragState) return;
      this.dropState = { section, index: null };
    },
    onSectionDrop(section) {
      this.onDrop(section, null);
    },
    onDrop(section, index) {
      if (!this.dragState) return;

      const sourceList = this.getSectionList(this.dragState.section);
      const targetList = this.getSectionList(section);
      const sourceIndex = this.dragState.index;
      const sourceField = sourceList?.[sourceIndex];

      if (!sourceList || !targetList || sourceIndex < 0 || sourceIndex >= sourceList.length) {
        this.clearDragState();
        return;
      }
      if (
        section === "individual" &&
        this.dragState.section === "shared" &&
        !this.canMoveToIndividual(sourceField)
      ) {
        this.clearDragState();
        return;
      }

      const [movedItem] = sourceList.splice(sourceIndex, 1);
      let targetIndex = typeof index === "number" ? index : targetList.length;

      if (sourceList === targetList && sourceIndex < targetIndex) {
        targetIndex -= 1;
      }

      targetList.splice(targetIndex, 0, movedItem);
      this.clearDragState();
    },
    onDragEnd() {
      this.clearDragState();
    },
    clearDragState() {
      this.dragState = null;
      this.dropState = { section: null, index: null };
    },
    getDropClass(section, index) {
      if (!this.dragState) return "";
      return this.dropState.section === section && this.dropState.index === index
        ? "assignments-fields-modal__item--drop-target"
        : "";
    },
    canMoveToIndividual(fieldName) {
      return this.individualEligibleFields.includes(fieldName);
    },
    isItemDraggable(section, fieldName) {
      if (section === "shared" && !this.canMoveToIndividual(fieldName)) return false;
      return true;
    },
  },
};
</script>

<style scoped lang="scss">
.assignments-fields-modal {
  padding: 16px 16px 10px;
  background-color: #f5f5f5;
}

.assignments-fields-modal__header {
  display: flex;
  align-items: center;
  color: #1f1f1f;
}

.assignments-fields-modal__content {
  margin-top: 12px;
}

.assignments-fields-modal__section-title {
  font-size: 16px;
  line-height: 20px;
  color: #555;
}

.assignments-fields-modal__list {
  border: 1px solid #d5d8dd;
  border-radius: 4px;
  background-color: #fff;
  min-height: 48px;
  overflow: hidden;
}

.assignments-fields-modal__item {
  min-height: 44px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid #eceff3;
  font-size: 13px;
  color: #212121;
  cursor: grab;
  user-select: none;
}

.assignments-fields-modal__item:last-child {
  border-bottom: none;
}

.assignments-fields-modal__item--drop-target {
  box-shadow: inset 0 2px 0 #1976d2;
}

.assignments-fields-modal__item--locked {
  cursor: not-allowed;
  opacity: 0.65;
}

.assignments-fields-modal__drag-icon {
  color: #8b8f98;
}
</style>
